import type {
  AiRuleDto,
  CalendarStripItem,
  CardDetailDto,
  CardDto,
  Destination,
  EnvelopeResponse,
  PiggybankResponse,
  PreviewDto,
  TodayResponse,
  UserSettings,
  WarningDto,
} from '@konvert/shared';
import { initData } from './telegram.js';

const BASE = import.meta.env.VITE_API_URL ?? '';

/**
 * What an applied command hands back. The warnings matter: the day limit is a
 * warning now, not a refusal (decision of 2026-09-29).
 */
export interface AppliedResult {
  status?: 'applied';
  operation_id: string;
  card_ids?: string[];
  warnings?: WarningDto[];
}

export interface ApiFailure {
  code: string;
  message: string;
  details?: { preview?: PreviewDto; reasons?: Array<{ code: string; message: string }> };
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly payload: ApiFailure,
  ) {
    super(payload.message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      // Only declare a JSON body when there actually is one.
      ...(init.body === undefined ? {} : { 'content-type': 'application/json' }),
      authorization: `tma ${initData()}`,
      ...(init.headers ?? {}),
    },
  });
  if (!response.ok) {
    const payload = (await response.json().catch(() => ({
      code: 'INTERNAL',
      message: 'Что-то пошло не так.',
    }))) as ApiFailure;
    throw new ApiError(response.status, payload);
  }
  return (await response.json()) as T;
}

export const api = {
  today: () => request<TodayResponse>('/api/today'),
  day: (date: string) => request<TodayResponse>(`/api/envelopes/day/${date}`),
  envelope: (kind: string, date?: string) =>
    request<EnvelopeResponse>(`/api/envelopes/${kind}${date ? `?date=${date}` : ''}`),
  strip: (from: string, to: string) =>
    request<CalendarStripItem[]>(`/api/calendar-strip?from=${from}&to=${to}`),
  card: (id: string) => request<CardDetailDto>(`/api/cards/${id}`),
  carryover: () => request<{ cards: CardDto[] }>('/api/carryover'),
  piggybank: () => request<PiggybankResponse>('/api/piggybank'),
  settings: () => request<{ settings: UserSettings; timezone: string }>('/api/settings'),

  assistantHistory: () =>
    request<{
      messages: Array<{
        id: string;
        role: 'user' | 'assistant';
        text: string;
        proposal_id: string | null;
        created_at: string;
      }>;
    }>('/api/assistant/messages'),
  assistantSend: (text: string, supersedes?: string) =>
    request<{ text: string; proposalId: string | null; preview: PreviewDto | null }>(
      '/api/assistant/messages',
      { method: 'POST', body: JSON.stringify({ text, supersedes }) },
    ),
  proposals: () =>
    request<{
      proposals: Array<{ id: string; origin: string; status: string; preview: PreviewDto }>;
    }>('/api/proposals'),
  applyProposal: (id: string, force = false) =>
    request<{ operation_id: string }>(`/api/proposals/${id}/apply`, {
      method: 'POST',
      body: JSON.stringify({ force }),
    }),
  rejectProposal: (id: string) =>
    request<{ status: string }>(`/api/proposals/${id}/reject`, { method: 'POST' }),

  rules: () => request<{ rules: AiRuleDto[] }>('/api/rules'),
  createRule: (payload: { kind: 'constraint' | 'style'; text: string; spec?: unknown }) =>
    request<AiRuleDto>('/api/rules', { method: 'POST', body: JSON.stringify(payload) }),
  setRuleStatus: (id: string, status: 'active' | 'disabled' | 'archived') =>
    request<AiRuleDto>(`/api/rules/${id}`, { method: 'PATCH', body: JSON.stringify({ status }) }),

  createCard: (payload: {
    kind: string;
    title: string;
    body?: string | null;
    fields?: Record<string, unknown>;
    destination?: Destination;
    force?: boolean;
    reason?: string;
  }) => request<AppliedResult>('/api/cards', {
    method: 'POST',
    body: JSON.stringify(payload),
  }),
  patchCard: (id: string, patch: Record<string, unknown>, expectedVersion?: number) =>
    request<AppliedResult>(`/api/cards/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ patch, expected_version: expectedVersion }),
    }),
  move: (id: string, destination: Destination, force = false, reason?: string) =>
    request<AppliedResult>(`/api/cards/${id}/move`, {
      method: 'POST',
      body: JSON.stringify({ destination, force, reason }),
    }),
  complete: (id: string, actualMinutes?: number | null) =>
    request<{ operation_id: string }>(`/api/cards/${id}/complete`, {
      method: 'POST',
      body: JSON.stringify({ actual_minutes: actualMinutes ?? null }),
    }),
  drop: (id: string) =>
    request<{ operation_id: string }>(`/api/cards/${id}/drop`, { method: 'POST' }),
  addNote: (id: string, text: string) =>
    request<{ operation_id: string }>(`/api/cards/${id}/notes`, {
      method: 'POST',
      body: JSON.stringify({ text }),
    }),
  setWaiting: (id: string, waitingFor: string, checkOn: string) =>
    request<{ operation_id: string }>(`/api/cards/${id}/waiting`, {
      method: 'POST',
      body: JSON.stringify({ waiting_for: waitingFor, check_on: checkOn }),
    }),
  setDayMode: (date: string, mode: string) =>
    request<{ operation_id: string }>(`/api/days/${date}/mode`, {
      method: 'POST',
      body: JSON.stringify({ mode }),
    }),
  undo: (operationId: string) =>
    request<{ operation_id: string }>(`/api/operations/${operationId}/undo`, { method: 'POST' }),
};
